import { IContextualMenuItem } from 'office-ui-fabric-react';
import { EstimatedChargeCalculationMode, ServerEntityType } from '../../entities/common';
import { TenantState } from '../../store/Tenant';

interface Entity {
    settings?: {
        estimatedChargeCalculationMode: EstimatedChargeCalculationMode;
    };
    isEditable: boolean;
}

interface BuildMenuItemParams<T extends Entity> {
    entity: T;
    tenant: TenantState;
    entityType: ServerEntityType;
    onModeChange: (mode: EstimatedChargeCalculationMode) => void;
}

/**
 * Builds the "Estimated Charge Calculation" contextual menu item for Portfolio/Program headers.
 * This menu item is only shown when the resource planning level matches the entity type.
 */
export function buildEstimatedChargeCalculationMenuItem<T extends Entity>(
    params: BuildMenuItemParams<T>
): IContextualMenuItem | undefined {
    const { entity, tenant, entityType, onModeChange } = params;

    const currentMode = entity.settings?.estimatedChargeCalculationMode ?? EstimatedChargeCalculationMode.UseChargeRate;

    // Only show if resource planning level matches the entity type or for Challenges
    if (tenant.resourcePlanningSettings?.resourcePlanningLevel !== entityType && entityType !== ServerEntityType.Challenge) {
        return undefined;
    }

    return {
        key: 'estimatedChargeCalculation',
        name: 'Estimated Charge Calculation',
        iconProps: { iconName: 'PPMXSectionResourcePlan' },
        disabled: !entity.isEditable,
        subMenuProps: {
            items: [
                {
                    key: 'useChargeRate',
                    name: 'Use Charge Rate in the resource plan',
                    canCheck: true,
                    checked: currentMode === EstimatedChargeCalculationMode.UseChargeRate,
                    onClick: () => {
                        if (currentMode !== EstimatedChargeCalculationMode.UseChargeRate) {
                            onModeChange(EstimatedChargeCalculationMode.UseChargeRate);
                        }
                    }
                },
                {
                    key: 'useCostRateTables',
                    name: 'Use Cost Rate Tables in the resource plan',
                    canCheck: true,
                    checked: currentMode === EstimatedChargeCalculationMode.UseCostRateTables,
                    onClick: () => {
                        if (currentMode !== EstimatedChargeCalculationMode.UseCostRateTables) {
                            onModeChange(EstimatedChargeCalculationMode.UseCostRateTables);
                        }
                    }
                }
            ]
        }
    };
}
